/**
 * @file CookiesComponent.tsx
 * @description
 * -----------------------------------------------------
 * Componente de Cookies
 * -----------------------------------------------------
 * @version 0.0.1a
 * @created 2025-07-27
 * @modifies 2025-07-30
 * @author Correcta
 * @author Mohammed Derfoufi
 * @copyright © Correcta
 * @license Private
 * */

"use client";

import ButtonComponent from "@/components/ui/ButtonComponent";
import ImageComponent from "@/components/ui/ImageComponent";
import {
  acceptCookies,
  deleteCookie,
  rejectCookies,
  savePreferenceCookies,
} from "@/lib/cookieUtils";
import { useState } from "react";
import { BUTTON_STATUS, TYPE_BUTTON } from "@/constants/button";
import { BUTTON_LABELS } from "@/constants/buttonLabels";
import { Link } from "@/i18n/routing";
import { useTranslations } from "next-intl";
import { ArrowLeft, ChevronDown } from "lucide-react";
import ToggleComponent from "@/components/ui/ToggleComponent";
import { useTheme } from "@/context/ThemeProvider";

export default function CookiesComponent() {
  const cookiesT = useTranslations("Cookies");
  const labels = useTranslations("Labels");

  const [cookiesAccepted, setCookiesAccepted] = useState(false);
  const [cookiesManaged, setCookiesManaged] = useState(false);
  const [strictCookies, setStrictCookies] = useState(true);
  const { isDark } = useTheme();
  const [showStrictCookiesDescription, setShowStrictCookiesDescription] =
    useState(false);
  const [preferenceCookies, setPreferenceCookies] = useState(true);
  const [
    showPreferenceCookiesDescription,
    setShowPreferenceCookiesDescription,
  ] = useState(false);

  const handleAcceptCookies = () => {
    setCookiesAccepted(true);
    acceptCookies().catch(() => {
      setCookiesAccepted(false);
    });
    savePreferenceCookies(true).catch(() => {
      setPreferenceCookies(true);
    });
  };

  const handleRejectCookies = () => {
    setCookiesAccepted(true);
    rejectCookies().catch(() => {
      setCookiesAccepted(false);
    });
    savePreferenceCookies(false).catch(() => {
      setPreferenceCookies(false);
    });
  };

  const handlePreferenceCookies = () => {
    if (preferenceCookies) {
      setPreferenceCookies(false);
      savePreferenceCookies(false).catch(() => {
        setPreferenceCookies(true);
      });
      deleteCookie("NEXT_LOCALE").catch(() => {
        setPreferenceCookies(true);
      });
    } else {
      setPreferenceCookies(true);
    }
  };

  if (cookiesAccepted) return null;

  return (
    <section className="cookies">
      <section className="cookies__image">
        <ImageComponent
          src={
            isDark ? "/assets/svg/cookies-white.svg" : "/assets/svg/cookies.svg"
          }
          alt={"cookiesImage"}
          zoomable={false}
        />
      </section>
      {!cookiesManaged ? (
        <section className="cookies__content">
          <h6>{cookiesT("title")}</h6>
          <p>
            {cookiesT("description")}{" "}
            <Link
              href="https://correcta.es/politica-de-cookies/"
              target="_blank"
            >
              {cookiesT("cookieLink")}
            </Link>
          </p>
          <section className="cookies__content__buttons cookies__content__buttons--desktop">
            <ButtonComponent
              label={BUTTON_LABELS.MANAGE_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.NO_OUTLINE}
              onClick={() => setCookiesManaged(true)}
            />
            <ButtonComponent
              label={BUTTON_LABELS.REJECT_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.ERROR}
              onClick={handleRejectCookies}
            />
            <ButtonComponent
              label={BUTTON_LABELS.ACCEPT_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.PRIMARY}
              onClick={handleAcceptCookies}
            />
          </section>
          <section className="cookies__content__buttons cookies__content__buttons--phone">
            <ButtonComponent
              label={BUTTON_LABELS.REJECT_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.ERROR}
              onClick={handleRejectCookies}
            />
            <ButtonComponent
              label={BUTTON_LABELS.ACCEPT_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.PRIMARY}
              onClick={handleAcceptCookies}
            />
            <ButtonComponent
              label={BUTTON_LABELS.MANAGE_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.NO_OUTLINE}
              onClick={() => setCookiesManaged(true)}
            />
          </section>
        </section>
      ) : (
        <>
          <article className="cookies__managed">
            <h6>{cookiesT("manage")}</h6>
            <section className="cookies__container--togle">
              <section className="flex__row">
                <ToggleComponent
                  id={"stricCookies"}
                  name={"stricCookies"}
                  checked={strictCookies}
                  disabled={true}
                  onChange={() => {
                    setStrictCookies(!strictCookies);
                  }}
                  showActivationText={false}
                />
                <span className="label">{labels("labelStrictCookies")}</span>
                <ButtonComponent
                  type={TYPE_BUTTON}
                  status={BUTTON_STATUS.NO_OUTLINE}
                  onClick={() =>
                    setShowStrictCookiesDescription(
                      !showStrictCookiesDescription,
                    )
                  }
                >
                  <ChevronDown />
                </ButtonComponent>
              </section>
              {showStrictCookiesDescription && (
                <p>{cookiesT("descriptionStrictCookies")}</p>
              )}
            </section>

            <section className="cookies__container--togle">
              <section className="flex__row">
                <ToggleComponent
                  id={"preferenceCookies"}
                  name={"preferenceCookies"}
                  checked={preferenceCookies}
                  onChange={() => handlePreferenceCookies()}
                  showActivationText={false}
                />
                <span className="label">
                  {labels("labelPreferenceCookies")}
                </span>
                <ButtonComponent
                  type={TYPE_BUTTON}
                  status={BUTTON_STATUS.NO_OUTLINE}
                  onClick={() =>
                    setShowPreferenceCookiesDescription(
                      !showPreferenceCookiesDescription,
                    )
                  }
                >
                  <ChevronDown />
                </ButtonComponent>
              </section>
              {showPreferenceCookiesDescription && (
                <p>{cookiesT("descriptionPreferenceCookies")}</p>
              )}
            </section>
          </article>
          <section className="cookies__managed__buttons">
            <ButtonComponent
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.SECONDARY}
              onClick={() => setCookiesManaged(false)}
              label={BUTTON_LABELS.BACK}
            >
              <ArrowLeft />
            </ButtonComponent>
            <ButtonComponent
              label={BUTTON_LABELS.REJECT_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.ERROR}
              onClick={handleRejectCookies}
            />
            <ButtonComponent
              label={BUTTON_LABELS.ACCEPT_COOKIES}
              type={TYPE_BUTTON}
              status={BUTTON_STATUS.PRIMARY}
              onClick={handleAcceptCookies}
            />
          </section>
        </>
      )}
    </section>
  );
}
